import { AppDeepLinks } from '../../scripts/appDeepLinks.mjs'

/**
 * Maps the page the visitor is on to the deep link the install banner
 * should open the app at (TASK-590) — the Android counterpart of the
 * per-page `app-argument` the prerender pipeline writes into the iOS
 * Smart App Banner.
 *
 * URL-only by design: the banner mounts in `LocaleLayout`, above the
 * schema provider, so the station-slug set is not available to validate
 * against. What a path segment must look like to be a station slug IS
 * known (`SiteUrls`' lowercase-hyphen shape), and anything that fails
 * that shape — a NAPTAN id on `/station/<ref>`, a schema id, a typo —
 * degrades to the map home, the app's landing screen, rather than to a
 * link that resolves to nothing.
 *
 * The curated-journey pair (`/routes/<from>-to-<to>`) is split at the
 * LEFTMOST `-to-` seam. `parseRoutePairSlug` proves the seam ambiguous
 * in principle and resolves it against the slug set; without the set,
 * leftmost matches how `SiteUrls.routePath` minted every current URL —
 * no station slug contains `-to-` today (asserted against schema.json by
 * the test file). If one ever does, the pathological pair degrades to a
 * route link with an unresolvable slug, which the apps treat as an
 * unfilled planner field — never a crash.
 */

/** The shape `SiteUrls` mints station slugs in. */
const SLUG_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

const PAIR_SEPARATOR = '-to-'

function stationOrMap(ref: string | undefined): string {
  if (ref !== undefined && SLUG_SHAPE.test(ref)) return AppDeepLinks.station(ref)
  return AppDeepLinks.map()
}

function routePair(pair: string | undefined): string {
  if (pair === undefined) return AppDeepLinks.map()
  const seam = pair.indexOf(PAIR_SEPARATOR)
  if (seam === -1) return AppDeepLinks.map()
  const fromSlug = pair.slice(0, seam)
  const toSlug = pair.slice(seam + PAIR_SEPARATOR.length)
  if (!SLUG_SHAPE.test(fromSlug) || !SLUG_SHAPE.test(toSlug)) return AppDeepLinks.map()
  return AppDeepLinks.route(fromSlug, toSlug)
}

function routePlanner(search: string): string {
  const params = new URLSearchParams(search)
  const fromSlug = params.get('from') ?? undefined
  const toSlug = params.get('to') ?? undefined
  return AppDeepLinks.route(fromSlug, toSlug)
}

/**
 * `london-tube://` deep link for the page at `pathname` + `search`.
 *
 * `pathname` is the locale-prefixed product URL (`/en/stations/…`) — the
 * banner renders inside `LocaleLayout`, which guarantees a valid leading
 * locale segment, so the first segment is dropped unconditionally.
 */
export function installBannerDeepLink(pathname: string, search: string): string {
  const [, kind, ...rest] = pathname.split('/').filter((segment) => segment !== '')
  switch (kind) {
    case 'stations':
    case 'station':
      return stationOrMap(rest[0])
    case 'routes':
      return routePair(rest[0])
    case 'route':
      return routePlanner(search)
    default:
      return AppDeepLinks.map()
  }
}
